/**
 * classifyPdfPages.ts
 *
 * Heuristic classifier that turns raw PDF page text into a suggested
 * PresentationSlot label + type. Works offline (no API call), fast enough
 * for 68+ pages.
 *
 * The rules are tuned for Dutch networking-club PDFs (Netwerkend Friesland
 * style) but degrade gracefully: unknown pages get type 'pdf_page' and
 * use the first few words as label.
 */

import type { PresentationSlot, PresentationSlotType } from './networkClubTypes';
import type { PdfPageInfo } from './pdfPageExtractor';

// ── keyword → slot type mapping ──────────────────────────────────────────────

interface Rule {
  keywords: RegExp;
  type: PresentationSlotType;
  label: string;
  durationMinutes: number;
  confidence: number;
}

const RULES: Rule[] = [
  { keywords: /welkom|introductie|opening/i,          type: 'intro',       label: 'Intro',               durationMinutes: 2, confidence: 0.85 },
  { keywords: /agenda\b/i,                            type: 'pdf_page',    label: 'Agenda',              durationMinutes: 2, confidence: 0.9 },
  { keywords: /pauze|break|koffie|bakje\s*tijd/i,     type: 'pauze',       label: 'Pauze',               durationMinutes: 5, confidence: 0.9 },
  { keywords: /pitch|voorstellen\s*&?\s*pitch/i,      type: 'pdf_page',    label: 'Voorstellen & Pitchen',durationMinutes: 1, confidence: 0.7 },
  { keywords: /presentatie|spreker|gastspreker/i,      type: 'presentatie', label: 'Presentatie',         durationMinutes: 10, confidence: 0.8 },
  { keywords: /aanbevel/i,                            type: 'pdf_page',    label: 'Aanbevelingen',       durationMinutes: 3, confidence: 0.75 },
  { keywords: /informeren\s*gasten|gasten\s*info/i,   type: 'pdf_page',    label: 'Informeren gasten',   durationMinutes: 2, confidence: 0.75 },
  { keywords: /afsluiting|einde|tot\s*ziens|bedankt/i,type: 'einde',       label: 'Einde bijeenkomst',   durationMinutes: 2, confidence: 0.8 },
  { keywords: /netwerkmoment|netwerk\s*borrel/i,      type: 'pauze',       label: 'Netwerkmoment',       durationMinutes: 5, confidence: 0.7 },
];

function classify(text: string): { type: PresentationSlotType; label: string; durationMinutes: number; confidence: number } {
  const normalized = text.replace(/\s+/g, ' ').trim();
  for (const rule of RULES) {
    if (rule.keywords.test(normalized)) {
      return { type: rule.type, label: rule.label, durationMinutes: rule.durationMinutes, confidence: rule.confidence };
    }
  }
  // Fallback: use first meaningful words as label
  const words = normalized.split(/\s+/).slice(0, 5).join(' ');
  return {
    type: 'pdf_page',
    label: words || `Pagina`,
    durationMinutes: 1,
    confidence: 0,
  };
}

/**
 * Turn extracted PDF pages into an initial set of PresentationSlots.
 * Each PDF page becomes one slot; the caller can later insert pitch/pauze
 * slots between them.
 */
export function classifyPages(pages: PdfPageInfo[]): PresentationSlot[] {
  return pages.map((page, i) => {
    const c = classify(page.textContent);
    return {
      id: `page_${page.pageNumber}`,
      type: c.type,
      pageNumber: page.pageNumber,
      label: c.label,
      durationMinutes: c.durationMinutes,
      enabled: true,
      order: i,
      aiConfidence: c.confidence,
    };
  });
}

/**
 * Create a new insertable slot (pitch, pauze, etc.) that is not tied to a PDF page.
 */
export function createInsertSlot(
  type: PresentationSlotType,
  afterOrder: number,
): PresentationSlot {
  const ts = Date.now();
  const defaults: Record<string, { label: string; dur: number }> = {
    pitch_leden:  { label: 'Pitch leden',  dur: 9 },
    pitch_gasten: { label: 'Pitch gasten', dur: 9 },
    presentatie:  { label: 'Presentatie',  dur: 10 },
    pauze:        { label: 'Pauze',        dur: 5 },
    intro:        { label: 'Intro',        dur: 3 },
    einde:        { label: 'Einde bijeenkomst', dur: 2 },
  };
  const d = defaults[type] ?? { label: type, dur: 2 };
  return {
    id: `insert_${type}_${ts}`,
    type,
    label: d.label,
    durationMinutes: d.dur,
    enabled: true,
    order: afterOrder + 0.5, // caller must re-index orders after insert
  };
}

/** Re-index slot orders to be sequential integers. */
export function reindexSlots(slots: PresentationSlot[]): PresentationSlot[] {
  return [...slots]
    .sort((a, b) => a.order - b.order)
    .map((s, i) => ({ ...s, order: i }));
}
